/* eslint-disable prefer-destructuring */
import React, { useState, useEffect, useMemo } from 'react';
import { Tag, Image } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { makeStyles } from '@material-ui/core';
import { isCurrentProjectBuildy, isCurrentProjectStimull } from 'src/util/projectCheckers';
import { getCurrentUser, getUserRoleIsUser } from 'src/util/isCurrentUserRoot';
import Modal from '../../component/Modal/Modal';
import CButton from '../../component/Button';
import { fetchItems, removeItemAction, downloadItemsToCsvAction } from '../../actions/ItemAction';
import InnerHeader from '../../component/InnerHeader';
import NoxTable from '../../component/Table';
import Copy from '../../component/Copy';
import formatDate from '../../helpers/formatDate';
import { MultipleSelect } from '../../component/Select';

const useStyles = makeStyles({
  featuredOn: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    fontFamily: 'Poppins',
    fontSize: '18px',
    fontStyle: 'normal',
    fontWeight: 500,
    transform: 'translateY(-3px)'
  },
  garbage: {
    cursor: 'pointer',
    left: '8px',
    position: 'absolute',
    top: '0px'
  },
  passwordWrapper: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center'
  },
  root: {
    ' & .MuiInputBase-root': {
      border: '1px solid #CAD8FF',
      borderRadius: '12px',
      color: 'black',
      padding: '14px 16px'
    },
    '& .MuiFormLabel-root.Mui-focused': {
      color: '#9BA0AD'
    },
    '& .MuiInput-inputMultiline': {
      alignItems: 'flex-start',
      height: '110px'
    },
    '& .MuiInput-underline:before': {
      display: 'none'
    },
    '& .MuiInputBase-input': {
      fontSize: '12px',
      padding: '0px'
    },

    '& .MuiInputLabel-formControl': {
      fontSize: '12px',
      left: '10px',
      top: '7px'
    },
    '& .MuiInputLabel-shrink': {
      transform: 'translate(-10px, -10px) scale(0.83)'
    }
  }
});

const Items = () => {
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState('All');
  const [featuredOn, setFeaturedOn] = useState('All');
  const [itemType, setItemType] = useState('All');
  const [currentUser, setCurrentUser] = useState(null);

  const dispatch = useDispatch();
  const items = useSelector(({ item }) => item.items);
  const classes = useStyles();

  useEffect(() => {
    const fetchUser = async () => {
      const user = await getCurrentUser();
      setCurrentUser(user);
    };

    fetchUser();
  }, []);

  const columns = [
    {
      dataIndex: 'link',
      key: 'link',
      render: (link) => (
        <Image height={40} src={link} style={{ objectFit: 'contain' }} width={40} />
      ),
      title: () => {
        return <div style={{ textAlign: 'center' }}>Preview</div>;
      }
    },
    {
      dataIndex: 'title',
      key: 'title',
      render: (title) => {
        return <Copy text={title} />;
      },
      sorter: (a, b) => a.title.localeCompare(b.title),
      title: 'Title'
    },
    {
      dataIndex: 'price',
      key: 'price',
      render: (price) => {
        return <Copy text={price} />;
      },
      sorter: (a, b) => a.price.localeCompare(b.price),
      title: 'Price'
    },
    isCurrentProjectBuildy && {
      dataIndex: 'wsPrice',
      key: 'wsPrice',
      render: (wsPrice) => {
        return <Copy text={wsPrice} />;
      },
      sorter: (a, b) => a.wsPrice.localeCompare(b.wsPrice),
      title: 'WholeSale Price'
    },
    {
      dataIndex: 'id',
      key: 'id',
      render: (id) => {
        return <Copy text={id} />;
      },
      sorter: (a, b) => a.id.localeCompare(b.id),
      title: 'Item ID'
    },
    {
      dataIndex: 'seller',
      key: 'seller',
      render: (seller) => {
        return <Copy text={seller} />;
      },
      sorter: (a, b) => a.seller.localeCompare(b.seller),
      title: 'Seller'
    },
    {
      dataIndex: 'category',
      key: 'category',
      render: (category) => {
        return <Copy text={category} />;
      },
      sorter: (a, b) => a.category.localeCompare(b.category),
      title: 'Category'
    },
    {
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const statusStyles = {
          backgroundColor: 'rgba(65, 204, 156, 0.15)',
          border: 'none',
          color: '#41CC9C',
          width: '100%'
        };

        if (status === 'Sold') {
          statusStyles.backgroundColor = 'rgba(155, 160, 173, 0.15)';
          statusStyles.color = '#9BA0AD';
        } else if (status === 'Deleted') {
          statusStyles.backgroundColor = '#f30000';
          statusStyles.color = '#fcf7f7';
        }

        return (
          <Tag key={status} style={statusStyles}>
            {status}
          </Tag>
        );
      },
      sorter: (a, b) => a.status.localeCompare(b.status),
      title: 'Status'
    },
    {
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (createdAt) => {
        return <Copy text={createdAt} />;
      },
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
      title: 'Created At'
    },
    {
      key: 'edit',
      render: (item) => {
        return (
          <Tag
            style={{
              overflow: 'hidden',
              padding: '0'
            }}
          >
            <Link
              style={{
                alignItems: 'center',
                color: 'inherit',
                display: 'flex',
                height: '100%',
                justifyContent: 'center',
                transition: 'none',
                width: '100%'
              }}
              to={`/ecommerce/items/${item.id}`}
            >
              edit
            </Link>
          </Tag>
        );
      }
    }
  ];

  const filterData = (data) => {
    const newData = [];
    if (selectedFilter !== 'All' && featuredOn !== 'All' && featuredOn !== 'All') {
      return data.filter(
        (item) =>
          item.status === selectedFilter &&
          item?.featuredOn?.includes(featuredOn) &&
          item.type === itemType
      );
    }
    if (selectedFilter !== 'All') {
      newData.push(data.filter((item) => item.status === selectedFilter));
    }
    if (featuredOn !== 'All') {
      newData.push(data.filter((item) => item?.featuredOn?.includes(featuredOn)));
    }
    if (itemType !== 'All') {
      newData.push(data.filter((item) => item?.type === itemType));
    }

    return newData.length ? newData[0] : data;
  };

  const mapToTable = (items) => {
    const data = [];
    let filteredArray = items;

    if (!items || !items.length) {
      return data;
    }

    if (isCurrentProjectBuildy && getUserRoleIsUser()) {
      filteredArray = items.filter((item) => {
        return item.userId === currentUser.tags.seller;
      });
    }

    filteredArray.forEach(
      (
        {
          _id,
          imagesUrls,
          title,
          price,
          user,
          createdAt,
          category,
          soldOut,
          deleted,
          featuredOn,
          type,
          options
        },
        index
      ) => {
        const date = formatDate(createdAt);
        let link = '';
        if (imagesUrls?.length) {
          link = imagesUrls[0];
        }

        let status = 'For Sale';
        if (soldOut) {
          status = 'Sold';
        } else if (deleted) {
          status = 'Deleted';
        }

        let totalPrice = price;
        let wsPrice = 0;

        if (isCurrentProjectBuildy) {
          totalPrice = options[0].price;
          wsPrice = options[0].wsPrice;
        }
        data.push({
          category,
          createdAt: date,
          featuredOn,
          id: _id,
          key: index,
          link,
          price: `${totalPrice} ${isCurrentProjectStimull || isCurrentProjectBuildy ? '₾' : '$'}`,
          seller: user,
          status,
          title,
          type,
          wsPrice: `${wsPrice} ${isCurrentProjectBuildy ? '₾' : '$'}`
        });
      }
    );

    return data;
  };

  const onDownload = () => {
    dispatch(downloadItemsToCsvAction());
  };

  const onItemDelete = () => {
    selectedRowKeys.forEach((index) => {
      const item = data[index];

      dispatch(
        removeItemAction(item, () => {
          dispatch(fetchItems());
        })
      );
    });

    setSelectedRowKeys([]);
  };

  const currentProject = useSelector(({ project }) => project.currentProject);

  useEffect(() => {
    async function load() {
      dispatch(fetchItems());
    }

    if (currentProject) {
      load();
    }
  }, [currentProject, dispatch]);

  const data = mapToTable(items);

  const actions = [
    {
      disabled: false,
      onClick: onDownload,
      title: 'Download CSV',
      type: 'primary'
    }
  ];

  if (selectedRowKeys.length) {
    actions.unshift(
      {
        onClick: onItemDelete,
        title: 'Delete',
        type: 'secondary'
      },
      {
        onClick: () => setShowModal(true),
        title: 'Feature',
        type: 'secondary'
      }
    );
  }

  const names = ['Sellers/ Home Page', 'Hero/ Popular'];
  const filterList = ['All', 'For Sale', 'Sold', 'Deleted'];
  const featuredOnList = ['All', 'Carousel / Home Page', 'Product of the Day'];
  const filterItemType = useMemo(() => {
    return [
      'All',
      ...new Set(items?.map((item) => item?.type).filter((filteredItem) => filteredItem))
    ];
  }, [items]);

  const onFilterChange = (value, index) => {
    if (isCurrentProjectStimull) {
      if (index === 0) {
        setSelectedFilter(value);
      } else if (index === 1) {
        setItemType(value);
      }
    } else if (index === 0) {
      setSelectedFilter(value);
    } else if (index === 1) {
      setFeaturedOn(value);
    } else if (index === 2) {
      setItemType(value);
    }
  };

  const filteredData = useMemo(() => {
    return filterData(data);
  }, [selectedFilter, featuredOn, itemType, data]);

  return (
    <div
      style={{
        height: 'calc(100vh - 165px)',
        overflowY: 'scroll'
      }}
    >
      <InnerHeader subtitle={data?.length} title="Items" />
      <NoxTable
        actions={actions}
        columns={columns.filter((column) => column !== false)}
        data={filteredData}
        filterList={
          isCurrentProjectStimull
            ? [filterList, filterItemType]
            : [filterList, featuredOnList, filterItemType]
        }
        onFilterChange={onFilterChange}
        searchBy="title"
        selectedFilter={
          isCurrentProjectStimull
            ? [selectedFilter, itemType]
            : [selectedFilter, featuredOn, itemType]
        }
        selectedRowKeys={selectedRowKeys}
        setSelectedRowKeys={setSelectedRowKeys}
      />
      <Modal
        footerActions={
          <>
            <CButton
              onClick={() => setShowModal(false)}
              type="secondary"
              xstyle={{ height: '40px' }}
            >
              Cancel
            </CButton>
            <CButton
              // onClick={deleteUser}
              type="primary"
              xstyle={{ height: '40px' }}
            >
              Delete
            </CButton>
          </>
        }
        handleClose={() => {
          setShowModal(false);
        }}
        isModalOpen={showModal}
        styles={{
          '& .MuiDialogContent-root': {
            paddingBottom: '24px'
          },
          width: '400px'
        }}
        titleContent={<p className="modal-title">Feature</p>}
      >
        <div className={classes.featuredOn}>
          Featured On
          <MultipleSelect
            currentValue={featured || []}
            handleChange={(value) => {
              setFeatured(
                // On autofill we get a stringified value.
                typeof value === 'string' ? value.split('/') : value
              );
            }}
            selectOptions={names}
            styles={{ marginLeft: '8px', width: '180px' }}
            type="text"
          />
        </div>
      </Modal>
    </div>
  );
};

export default Items;
